import { Card, Grid, Icon, IconButton, InputLabel, Tooltip } from "@mui/material";
import MDInput from "components/MDInput";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import DataTable from "examples/Tables/DataTable";
import DefaultCell from "layouts/dashboards/sales/components/DefaultCell";
import { useCallback, useEffect, useRef, useState } from "react";
import { verifyVariable, infoAviso, successAlert, warningAlert } from "utils/Functions";
import { post, postNoResponse } from "utils/WebService";
import RisksCreateEdit from "./RisksCreateEdit";
import { ESCURO } from "assets/coresTema";

export default function RisksConsult() {
    const [controller] = useMaterialUIController();
    const { darkMode } = controller;

    const tableState = useRef({
        pageSize: 10,
        pageIndex: 0,
        sorting: [],
        filtered: [],
    });

    const [data, setData] = useState([]);
    const [pages, setPages] = useState(0);
    const [loadingTable, setLoadingTable] = useState(true);
    const [isCreateEdit, setIsCreateEdit] = useState(false);
    const [selectedId, setSelectedId] = useState(null);
    const [recordsTotal, setRecordsTotal] = useState(0);
    const [aplicar, setAplicar] = useState(false);

    const [filters, setFilters] = useState({
        descricao: '',
    });

    useEffect(() => {
        fetchData({ ...tableState });
    }, []);

    const fetchData = useCallback(({ pageSize, pageIndex, sorting, filtered }) => {
        setLoadingTable(true);

        const updatedTableState = {
            pageSize: pageSize ? pageSize : tableState.current.pageSize,
            pageIndex: (pageIndex == 0 || pageIndex) ? pageIndex : tableState.current.pageIndex,
            sorting: sorting ? sorting : tableState.current.sorting,
            filtered: filtered ? filtered : tableState.current.filtered,
        };

        tableState.current = updatedTableState;

        post("/risco/list", {
            page: updatedTableState.pageIndex,
            pageSize: updatedTableState.pageSize,
            sorted: updatedTableState.sorting,
            filtered: updatedTableState.filtered,
        }).then(({ data, pages, recordsTotal }) => {
            setPages(pages);
            setData(data);
            setRecordsTotal(recordsTotal);
            setLoadingTable(false);
            setAplicar(false);
        }).catch(() => {
            setLoadingTable(false)
            setAplicar(false);
        });

    }, []);

    const backFunction = () => {
        fetchData({ ...tableState });
        setIsCreateEdit(false);
        setSelectedId(null);
    }

    const handleDelete = (id) => {
        infoAviso(
            "Deseja realmente excluir esse registro?",
            async () => {
                await removeRisco(id);
            },
            () => null
        );
    };

    const removeRisco = async (id) => {
        if (id) {
            postNoResponse(`/risco/delete/${id}`)
                .then(() => {
                    successAlert("Risco removido com sucesso!", () => null);
                    fetchData({ ...tableState });
                })
                .catch(e => {
                    console.error(e);
                    warningAlert("Registro em uso, exclusão não permitida");
                });
        }
    };

    const userColumns = [
        {
            header: "descricao",
            accessorKey: "descricao",
            cell: info => <DefaultCell>{info.getValue() || ""}</DefaultCell>,
            isSorted: true
        },
        {
            header: "acoes",
            isSorted: false,
            cell: info => (
                <>
                    <Tooltip title="Editar">
                        <IconButton
                            onClick={() => {
                                setSelectedId(info.row.original.id);
                                setIsCreateEdit(true);
                            }}>
                            <MDTypography variant="h4">
                                <Icon color="inherit" sx={{ marginRight: 1 }}>edit</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                        <IconButton
                            onClick={() => handleDelete(info.row.original.id)}>
                            <MDTypography variant="h4">
                                <Icon color="error" sx={{ marginRight: 1 }}>delete</Icon>
                            </MDTypography>
                        </IconButton>
                    </Tooltip>
                </>
            ),
            width: "15%"
        },
    ];

    const handleApplyFilter = () => {
        let filtered = [];

        Object.keys(filters).forEach(filterKey => {
            if (verifyVariable(filters[filterKey])) {
                filtered.push({ id: filterKey, value: filters[filterKey] });
            }
        })
        setAplicar(true);
        fetchData({ ...tableState, pageIndex: 0, filtered });
    };

    const handleCleanFilter = () => {
        setFilters({
            descricao: '',
        })
        fetchData({ pageSize: 10, pageIndex: 0, sorting: [], filtered: [] });
    };

    return (
        <Grid container display="flex" justifyContent="center">
            {isCreateEdit &&
                <Grid item xs={12} md={12}>
                    <RisksCreateEdit
                        id={selectedId}
                        closeCreateEdit={backFunction} />
                </Grid>
            }
            <Grid item xs={12} md={12} sx={{ display: isCreateEdit ? "none" : "" }}>
                <Card
                    variant="outlined"
                    sx={{
                        px: 2,
                        py: 1.5,
                        mb: 2,
                        background: darkMode ? ESCURO.cartao : "#fafafa",
                        borderColor: darkMode ? ESCURO.divisoria : "#ddd",
                    }}
                >
                    <Grid container spacing={1}>
                        <Grid item xs={12} sm={3}>
                            <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Descrição</InputLabel>
                            <MDInput
                                size="small"
                                placeholder="Informe a descrição..."
                                value={filters.descricao}
                                onChange={({ target }) => setFilters({ ...filters, descricao: target.value })}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter') {
                                        handleApplyFilter();
                                    }
                                }}
                                fullWidth
                            />
                        </Grid>

                        <Grid item xs={12} display="flex" alignItems="center" justifyContent="space-between" pt={1}>
                            <MDBox>
                                <MDButton
                                    size="medium"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={() => {
                                        setSelectedId(null);
                                        setIsCreateEdit(true);
                                    }}>
                                    Adicionar Risco
                                </MDButton>
                            </MDBox>
                            <MDBox>
                                <MDButton
                                    size="medium"
                                    sx={{ marginRight: 2 }}
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleCleanFilter}>
                                    Limpar filtro
                                </MDButton>
                                <MDButton
                                    size="medium"
                                    variant="contained"
                                    color="info"
                                    circular={false}
                                    iconOnly={false}
                                    onClick={handleApplyFilter}>
                                    Aplicar filtro
                                </MDButton>
                            </MDBox>
                        </Grid>
                    </Grid>
                </Card>
                <DataTable
                    loading={loadingTable}
                    columnData={userColumns}
                    data={data}
                    fetchData={fetchData}
                    totalPages={pages}
                    totalRecords={recordsTotal}
                    changeFilter={aplicar}
                    tableState={tableState} />
            </Grid>
        </Grid >
    );
}

